import { useCallback, useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "../../lib/api";
import PortalShell from "../../components/PortalShell";
import EmbeddedQuestion from "../../components/EmbeddedQuestion";
import MathsAnswer from "../../components/MathsAnswer";
import Calculator from "../../components/Calculator";
import { toast } from "sonner";
import {
  Loader2, ExternalLink, Camera, Check, Clock, ClipboardCheck, Hand, BookOpen,
  Trash2, X, CheckCircle2, Sparkles, Calculator as CalcIcon, Timer, MinusCircle,
  Ban as CalcOff,
  AlertTriangle, Lock,
} from "lucide-react";

/**
 * A student's assignments, and doing one.
 *
 * The question is shown in the page, framed from the bank's /embed route.
 * It is a frame rather than a second renderer: the bank draws questions with
 * the same component the tutor approved them in, and a copy here would drift
 * from that. The frame reports its own height, so a long question is not
 * clipped and a short one leaves no gap.
 *
 * Nothing on this page puts a mark on a student's behalf. The answer box stores
 * what they typed; the marks come from the mark scheme, awarded by them or by
 * their tutor.
 */

const BANK = process.env.REACT_APP_QBANK_URL || "https://qbank.geniuseducation.co.uk";


const mmss = (n) => {
  const s = Math.max(0, Math.round(n || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};


/**
 * The clock on a monthly test or a mock.
 *
 * **The server decides it; this only draws it.** Every heartbeat replaces the
 * number, and the local tick between beats is cosmetic - which is what makes
 * refreshing the page continue from where it was instead of handing out a
 * fresh twenty minutes.
 */
function Countdown({ timing }) {
  const [left, setLeft] = useState(timing.seconds_remaining ?? 0);

  useEffect(() => {
    setLeft(timing.seconds_remaining ?? 0);
  }, [timing.seconds_remaining, timing.deadline_at]);

  useEffect(() => {
    const t = setInterval(() => setLeft((n) => (n > 0 ? n - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);

  if (timing.expired) {
    const upload = timing.upload_seconds_remaining;
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 mb-5 flex items-start gap-2.5">
        <AlertTriangle size={17} className="text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm">
          <p className="font-semibold text-amber-900">
            Time is up{upload != null ? ` — ${mmss(upload)} left to photograph your working` : ""}
          </p>
          <p className="text-amber-800 mt-0.5">
            Your answers are locked, but you can still add photos. If you run out of time again
            you will be given another five minutes.
            {timing.extensions_used > 0 &&
              ` You have had ${timing.extensions_used} extra ${timing.extensions_used === 1 ? "window" : "windows"}.`}
          </p>
        </div>
      </div>
    );
  }

  const urgent = left <= 60;
  const soon = left <= 300;
  return (
    <div className={`rounded-xl border px-4 py-3 mb-5 flex items-center gap-3 ${
      urgent ? "border-red-300 bg-red-50" : soon ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
      <Timer size={17} className={urgent ? "text-red-600" : soon ? "text-amber-600" : "text-slate-400"} />
      <div className="flex-1">
        <p className={`font-mono text-xl font-semibold tabular-nums ${
          urgent ? "text-red-700" : soon ? "text-amber-800" : "text-slate-800"}`}>
          {mmss(left)}
        </p>
        <p className="text-xs text-slate-500">
          left of your {Math.round((timing.limit_seconds || 0) / 60)} minutes · one sitting
        </p>
      </div>
      {soon && (
        <p className="text-xs text-slate-600 max-w-[220px]">
          When the clock stops you will still have five minutes to photograph your working.
        </p>
      )}
    </div>
  );
}


/**
 * One tick every twenty seconds, naming the question on screen.
 *
 * It carries two things home: how long that question has been looked at, and
 * a request for the authoritative clock. One call rather than two because
 * both happen at the same moment.
 *
 * **A hidden tab credits nothing.** It resets the mark instead, so a laptop
 * left open on this page overnight does not report eight hours on question 3.
 * The server clamps each tick as well; this is the honest half of the same
 * agreement, and neither half is trusted alone.
 */
function useHeartbeat(id, codeRef, active, onBeat) {
  useEffect(() => {
    if (!active) return undefined;
    let mark = Date.now();
    const tick = () => {
      if (document.visibilityState !== "visible") {
        mark = Date.now();
        return;
      }
      const seconds = Math.round((Date.now() - mark) / 1000);
      mark = Date.now();
      api.post(`/my/assignments/${id}/heartbeat`,
               { question_code: codeRef.current || null, seconds })
        .then((r) => onBeat(r.data))
        .catch(() => { /* a dropped beat costs a few seconds, not the work */ });
    };
    const timer = setInterval(tick, 20000);
    return () => clearInterval(timer);
  }, [id, codeRef, active, onBeat]);
}


/** One photo, full size. A thumbnail cannot answer "is this readable?". */
function Lightbox({ src, onClose }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/90 flex items-center justify-center p-4" onClick={onClose}>
      <button type="button" onClick={onClose} className="absolute top-3 right-3 text-white/80 hover:text-white p-2">
        <X size={22} />
      </button>
      <img src={src} alt="Your working, full size" className="max-h-full max-w-full rounded-lg"
        onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

/**
 * The working a student photographed, on the page where they do the question.
 *
 * Fetched through the upload token rather than the login, so these are plain
 * <img> tags. The logged-in route for a photo needs an Authorization header,
 * which an <img> cannot send - that route is for the tutor's marking screen,
 * which fetches blobs. The token is already on this page: it is what the QR
 * code encodes and what the "Open upload page" link carries, so using it here
 * gives nothing away that was not already here.
 */
function WorkingPhotos({ token, ids, locked, onDelete, onOpen }) {
  if (!ids || ids.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 mb-1.5">
        Your working — tap to see it properly
      </p>
      <div className="flex flex-wrap gap-2">
        {ids.map((id) => {
          const src = `${BACKEND_URL}/api/upload/${token}/photo/${id}`;
          return (
            <div key={id} className="relative">
              <button type="button" onClick={() => onOpen(src)}
                className="block w-[86px] h-[86px] rounded-lg overflow-hidden border border-slate-300 bg-white">
                <img src={src} alt="Your working" className="w-full h-full object-cover" />
              </button>
              {!locked && (
                <button type="button" onClick={() => onDelete(id)} aria-label="Delete this photo"
                  className="absolute -top-1.5 -right-1.5 bg-white border border-slate-300 rounded-full p-1 shadow-sm text-red-600 hover:bg-red-50">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The mark scheme, in the page.
 *
 * It used to be a link to the question on the bank site, which was wrong twice
 * over: it showed the question again rather than the mark scheme, and the bank
 * refuses answers for any paper the tutor has not released publicly - so the
 * link led to a padlock.
 *
 * The pictures are proxied through this portal. The bank will not serve them
 * without the admin token, and the token must never reach a browser.
 */
function AnswerPanel({ assignmentId, code, kind }) {
  const [rows, setRows] = useState(null);
  const [srcs, setSrcs] = useState([]);
  const [err, setErr] = useState(null);
  // The server decides how long this stays on screen and when the clock
  // started; the page only counts it down. Doing the arithmetic here off a
  // server timestamp rather than keeping a timer in the browser means a
  // refresh does not buy another half minute.
  const [window_, setWindow] = useState(null);
  const [left, setLeft] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    api.get(`/my/assignments/${assignmentId}/answers/${code}`)
      .then((r) => { setRows(r.data[kind] || []); setWindow(r.data.window || null); })
      .catch((e) => setErr(e.response?.data?.detail || "Could not load it"));
  }, [assignmentId, code, kind, nonce]);

  useEffect(() => {
    if (!window_?.expires_at) return undefined;
    const ends = new Date(window_.expires_at).getTime();
    const tick = () => setLeft(Math.max(0, Math.ceil((ends - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [window_]);

  const hidden = left === 0;

  // Each picture is fetched with the login token attached and shown from an
  // object URL. A plain <img src> sends no Authorization header and the
  // server, correctly, refuses it.
  useEffect(() => {
    if (!rows || rows.length === 0) return undefined;
    let dead = false;
    const made = [];
    Promise.all(
      rows.map((_, i) =>
        api.get(`/my/assignments/${assignmentId}/answer-image/${code}/${kind}/${i}`,
                { responseType: "blob" })
          .then((r) => URL.createObjectURL(r.data))
          .catch(() => null)
      )
    ).then((urls) => {
      if (dead) { urls.forEach((u) => u && URL.revokeObjectURL(u)); return; }
      made.push(...urls.filter(Boolean));
      setSrcs(urls);
    });
    return () => { dead = true; made.forEach((u) => URL.revokeObjectURL(u)); };
  }, [rows, assignmentId, code, kind]);

  if (err) return <p className="text-sm text-red-700 mt-2">{err}</p>;
  if (!rows) return <p className="text-sm text-slate-400 mt-2">Loading…</p>;
  if (rows.length === 0) {
    return (
      <p className="text-sm text-slate-500 mt-2">
        There is no {kind === "mark_scheme" ? "mark scheme" : "model answer"} picture for this question.
      </p>
    );
  }

  // The answer hides itself again once the window closes. Not a punishment
  // and not a lock: the working was photographed and sealed at hand-in, so
  // there is nothing left to copy into. It is here so that marking is looking
  // and judging rather than transcribing with the answer sat open alongside.
  // Pressing the button opens a fresh window, and each one is counted.
  if (hidden) {
    return (
      <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-center">
        <p className="text-sm text-slate-600 mb-3">
          Time's up on that look. Give yourself the marks you think you earned —
          or take another look if you need one.
        </p>
        <button
          onClick={() => { setLeft(null); setWindow(null); setNonce((n) => n + 1); }}
          className="text-sm font-semibold text-slate-800 border border-slate-300
                     rounded-lg px-3 py-1.5 bg-white hover:bg-slate-50"
        >
          Show it again
        </button>
        {window_?.views > 1 && (
          <p className="text-xs text-slate-400 mt-2">
            You have looked at this {window_.views} times.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs uppercase tracking-wider font-bold text-slate-400">
          {kind === "mark_scheme" ? "Mark scheme" : "Model answer"}
        </p>
        {left !== null && (
          <p className={`text-xs font-semibold tabular-nums ${
            left <= 10 ? "text-amber-700" : "text-slate-400"}`}>
            {left}s
          </p>
        )}
      </div>
      {rows.map((r, i) => (
        srcs[i] ? (
          <img
            key={i}
            src={srcs[i]}
            alt={`${kind === "mark_scheme" ? "Mark scheme" : "Model answer"} for ${code}${r.part_label ? ` part ${r.part_label}` : ""}`}
            className="w-full rounded-lg border border-slate-200 bg-white"
          />
        ) : (
          <p key={i} className="text-sm text-slate-400">Loading the picture…</p>
        )
      ))}
      <p className="text-xs text-slate-400">
        Compare your working against this and give yourself the marks you earned.
      </p>
    </div>
  );
}

function Doing({ id, onBack }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [showing, setShowing] = useState({});
  const [big, setBig] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [calcFor, setCalcFor] = useState(null);
  const [timing, setTiming] = useState({ timed: false });
  const [missing, setMissing] = useState([]);
  const [confirmSkip, setConfirmSkip] = useState(null);
  // Which question is on screen, for the heartbeat. A ref rather than state
  // so that changing question does not restart the beat and throw away the
  // seconds since the last one.
  const onScreen = useRef(null);

  const load = useCallback(() => {
    api.get(`/my/assignments/${id}`)
      .then((r) => {
        setD(r.data);
        setDraft(r.data.answers || {});
        setTiming(r.data.timing || { timed: false });
        setMissing(r.data.missing_photos || []);
        if (!onScreen.current) onScreen.current = r.data.last_question_code || null;
      })
      .catch((e) => setErr(e.response?.data?.detail || "Could not open it"));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const applyBeat = useCallback((data) => {
    if (data?.timing) setTiming(data.timing);
    if (data?.missing_photos) setMissing(data.missing_photos);
  }, []);

  const beating = !!d && d.status !== "submitted";
  useHeartbeat(id, onScreen, beating, applyBeat);

  // "K4M-2P7" for a question with no parts, "K4M-2P7|a" for part (a). The
  // same shape the backend stores, so there is one answer to "where does
  // this answer live" rather than a translation in the middle.
  const fieldFor = (code, part) => (part ? `${code}|${part}` : code);

  const saveAnswer = (code, part) => {
    const field = fieldFor(code, part);
    api.post(`/my/assignments/${id}/answer`,
             { question_code: code, part_label: part || null, answer: draft[field] || "" })
      .then(() => toast.success("Answer saved"))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not save"));
  };

  const saveCalculator = (code, lines) =>
    api.post(`/my/assignments/${id}/calculator`, { question_code: code, lines })
      .then(() => { toast.success("Working kept"); load(); });

  const submit = () => {
    setBusy(true);
    api.post(`/my/assignments/${id}/submit`)
      .then(() => { toast.success("Handed in"); setConfirming(false); load(); })
      .catch((e) => {
        // The gate refuses with the questions it is waiting for, so say which
        // ones rather than "could not hand in".
        const detail = e.response?.data?.detail;
        if (detail && typeof detail === "object") {
          setMissing(detail.missing || []);
          toast.error(detail.error || "Photograph your working first");
        } else {
          toast.error(detail || "Could not hand in");
        }
        load();
      })
      .finally(() => setBusy(false));
  };

  /** "I did not do this one" — the honest way past the photograph gate. */
  const setNotAttempted = (code, value) => {
    api.post(`/my/assignments/${id}/not-attempted`,
             { question_code: code, not_attempted: value })
      .then(() => { load(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not save that"));
  };

  // Deleting goes through the upload token, which is the same route the phone
  // uses. One route for "remove a photo from this attempt", so a rule about
  // when that is allowed cannot be enforced in one place and forgotten in the
  // other.
  const removePhoto = (fileId) => {
    api.delete(`/upload/${d.upload_token}/${fileId}`)
      .then(() => load())
      .catch((e) => toast.error(e.response?.data?.detail || "Could not delete that photo"));
  };

  const request = (code, kind) => {
    api.post(`/my/assignments/${id}/request`, { question_code: code, kind })
      .then((r) => {
        // Three outcomes now, not two. A KS3 question has no mark scheme, so
        // its worked answer is released on hand-in and saying "asked your
        // tutor" would be a lie that leaves a child waiting for nothing.
        toast.success(
          r.data.status !== "released"
            ? "Asked your tutor — they will release it"
            : kind === "mark_scheme"
              ? "Mark scheme unlocked"
              : "Answer unlocked — have a look, then mark your work",
        );
        load();
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not ask"));
  };

  const selfMark = (code, marks) => {
    api.post(`/my/assignments/${id}/self-mark`, { question_code: code, marks })
      .then(() => load())
      .catch((e) => toast.error(e.response?.data?.detail || "Could not save that mark"));
  };

  if (err) return <p className="text-sm text-red-700">{err}</p>;
  if (!d) return <p className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 size={15} className="animate-spin" /> Loading…</p>;

  const submitted = d.status === "submitted";
  const uploadUrl = `${window.location.origin}/upload/${d.upload_token}`;
  const withWorking = d.question_codes.filter((c) => (d.working[c] || []).length > 0).length;
  // From the server, not worked out here: it is the same list the hand-in gate
  // refuses on, and it already allows for the questions declared not
  // attempted. Two implementations would eventually disagree, and the child
  // would be the one stuck between them.
  const missingPhotos = missing;
  const skipped = d.not_attempted || {};
  // The clock stops the typing, never the photographs.
  const locked = submitted || !!timing.answers_locked;
  const partsOf = (code) => ((d.meta || {})[code]?.parts) || [];
  const fieldsOf = (code) => {
    const ps = partsOf(code);
    return ps.length ? ps.map((p) => ({ part: p.label, marks: p.marks })) : [{ part: null, marks: (d.meta || {})[code]?.marks }];
  };
  const typed = d.question_codes.filter((c) =>
    fieldsOf(c).some((f) => String(draft[fieldFor(c, f.part)] || "").trim())).length;
  const statusOf = (code, kind) =>
    (d.requests.find((r) => r.question_code === code && r.kind === kind) || {}).status;

  return (
    <div>
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 mb-4">← My assignments</button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{d.title}</h1>
          <p className="text-sm text-slate-500 mt-0.5 capitalize">
            {d.kind}{d.due_date ? ` · due ${d.due_date}` : ""} ·{" "}
            {submitted ? "handed in" : "not handed in"}
          </p>
        </div>
        {!submitted && (
          <button onClick={submit} disabled={busy || missingPhotos.length > 0}
            title={missingPhotos.length > 0
              ? `Still waiting for a photo of ${missingPhotos.join(", ")}`
              : "Hand in your work"}
            className="inline-flex items-center gap-1.5 bg-slate-800 text-white text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-50">
            <Hand size={15} /> {busy ? "Handing in…" : "Hand in"}
          </button>
        )}
      </div>

      {!submitted && timing.timed && timing.started && <Countdown timing={timing} />}

      {!submitted && timing.timed && !timing.started && (
        <p className="rounded-xl border border-slate-200 bg-white px-4 py-3 mb-5 text-sm text-slate-600">
          <Timer size={15} className="inline mr-1.5 -mt-0.5 text-slate-400" />
          This is a timed paper: <strong>{Math.round((timing.limit_seconds || 0) / 60)} minutes</strong>,
          in one sitting. The clock starts when the questions load.
        </p>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5 flex flex-wrap items-center gap-4">
        <img
          src={`${BACKEND_URL}/api/qr/upload/${d.upload_token}.png`}
          alt="QR code to open the upload page on your phone"
          className="w-[108px] h-[108px] shrink-0 rounded border border-slate-200 bg-white"
        />
        <div className="flex-1 min-w-[180px]">
          <p className="font-semibold text-sm text-slate-800">Upload your working from your phone</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {withWorking} of {d.question_codes.length} questions have a photo. No password needed.
          </p>
        </div>
        <a href={uploadUrl} target="_blank" rel="noreferrer"
          className="text-sm inline-flex items-center gap-1.5 border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50">
          Open upload page <ExternalLink size={12} />
        </a>
      </div>

      {!submitted && (
        <p className="text-sm text-slate-500 mb-4">
          Mark schemes stay locked until you hand in, and your answers are checked then &mdash; not as you type,
          because a tick appearing one guess at a time would just be telling you the answer. Do the working on
          paper, photograph it, and type your answers here.
        </p>
      )}

      <div className="space-y-3">
        {d.question_codes.map((code, i) => {
          const photos = (d.working[code] || []).length;
          const msStatus = statusOf(code, "mark_scheme");
          const maStatus = statusOf(code, "model_answer");
          const mine = d.self_marks[code];
          const theirs = d.tutor_marks[code];
          const checked = (d.auto_detail || {})[code] || {};
          const autoFull = checked.complete && checked.possible > 0 && checked.marks === checked.possible;

          const notDone = !!skipped[code];
          const wanted = missingPhotos.includes(code);
          // What the paper says this question is worth, and therefore the
          // highest mark a child can honestly give themselves. Null where the
          // bank does not know - a question withdrawn from approval after
          // being set - and then 0-5 is a guess rather than a claim.
          const outOf = (d.meta || {})[code]?.marks ?? null;
          const marksLadder = outOf != null && outOf > 0
            ? Array.from({ length: outOf + 1 }, (_, n) => n)
            : [0, 1, 2, 3, 4, 5];

          return (
            // Touching anywhere in the card is what says "this is the question
            // I am on" - the heartbeat needs that, and a child who is working
            // on paper may never type in the box at all.
            <div key={code}
              onPointerDown={() => { onScreen.current = code; }}
              onFocusCapture={() => { onScreen.current = code; }}
              className={`rounded-xl border bg-white overflow-hidden ${
                wanted ? "border-amber-300" : notDone ? "border-slate-300" : "border-slate-200"}`}>
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center gap-3">
                <span className="text-xs text-slate-400 font-semibold">{i + 1}</span>
                <span className="font-mono text-sm font-semibold">{code}</span>
                {(d.meta || {})[code]?.marks != null && (
                  <span className="text-xs text-slate-500">{(d.meta || {})[code].marks} marks</span>
                )}
                {/* Calculator or not, per question, because a worksheet is
                    allowed to mix them - the tutor sets both on purpose so a
                    child practises each. Shown only where the bank knows:
                    `calculator` is true, false, or null for undetermined, and
                    telling a child they may use a calculator on a question
                    written without one is worse than saying nothing. */}
                {(d.meta || {})[code]?.calculator === false && (
                  <span className="text-xs inline-flex items-center gap-1 font-semibold text-rose-800 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
                    <CalcOff size={11} /> No calculator
                  </span>
                )}
                {(d.meta || {})[code]?.calculator === true && (
                  <span className="text-xs inline-flex items-center gap-1 text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
                    <CalcIcon size={11} /> Calculator
                  </span>
                )}
                {(d.meta || {})[code]?.topic && (
                  <span className="text-xs text-slate-400 truncate max-w-[180px]">{(d.meta || {})[code].topic}</span>
                )}
                {submitted && checked.marks > 0 && (
                  <span className="text-xs inline-flex items-center gap-1 font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                    <Sparkles size={11} />
                    {autoFull
                      ? `${checked.marks} of ${checked.total} \u2014 all correct`
                      : `${checked.marks} mark${checked.marks === 1 ? "" : "s"} confirmed`}
                  </span>
                )}
                {notDone && (
                  <span className="text-xs inline-flex items-center gap-1 font-semibold text-slate-600 bg-slate-100 border border-slate-300 rounded-full px-2 py-0.5">
                    <MinusCircle size={11} /> not attempted
                  </span>
                )}
                {!submitted && wanted && (
                  <span className="text-xs inline-flex items-center gap-1 font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5">
                    <Camera size={11} /> needs a photo
                  </span>
                )}
                <span className={`ml-auto text-xs inline-flex items-center gap-1 ${photos ? "text-green-700" : "text-slate-400"}`}>
                  <Camera size={12} /> {photos} photo{photos === 1 ? "" : "s"}
                </span>
              </div>

              <div className="p-4 space-y-3">
                <EmbeddedQuestion code={code} />

                <div className="space-y-2">
                  {fieldsOf(code).map(({ part, marks }) => {
                    const field = fieldFor(code, part);
                    const verdict = (checked.parts || {})[part || ""];
                    return (
                      <div key={field} className="flex flex-wrap items-start gap-2">
                        <label className="text-sm text-slate-600 font-medium min-w-[86px] self-start pt-1.5"
                          htmlFor={`a-${field}`}>
                          {part ? `Answer (${part})` : "Your answer"}
                          {marks != null && <span className="text-xs text-slate-400 font-normal"> · {marks}m</span>}
                        </label>
                        <MathsAnswer
                          id={`a-${field}`}
                          value={draft[field] || ""}
                          disabled={locked}
                          onChange={(v) => setDraft({ ...draft, [field]: v })}
                          onCommit={() => !locked && saveAnswer(code, part)}
                          onOpenCalculator={() => setCalcFor(code)}
                        />
                        {submitted && verdict === "correct" && (
                          <span className="text-xs inline-flex items-center gap-1 font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-2 py-1 self-start mt-1">
                            <Check size={12} /> correct
                          </span>
                        )}
                        {submitted && verdict === "unmatched" && (
                          <span className="text-xs text-slate-500 self-start pt-2">your tutor will check this one</span>
                        )}
                        {submitted && verdict === "not-checked" && (
                          <span className="text-xs text-slate-400 self-start pt-2">marked from your working</span>
                        )}
                      </div>
                    );
                  })}
                  {submitted && (
                    <p className="text-xs text-slate-500">
                      Some questions ask you to <em>show</em> something &mdash; those have no single answer, and the
                      marks are in your working.
                    </p>
                  )}
                </div>

                {/* What the calculator was asked, kept with the question. */}
                {(d.calc_tape || {})[code]?.length > 0 && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1 inline-flex items-center gap-1">
                      <CalcIcon size={11} /> Your calculator working
                    </p>
                    {d.calc_tape[code].map((line, n) => (
                      <div key={n} className="font-mono text-xs text-slate-600">{line}</div>
                    ))}
                  </div>
                )}

                <WorkingPhotos token={d.upload_token} ids={d.working[code] || []}
                  locked={submitted} onDelete={removePhoto} onOpen={setBig} />

                {/* The way out of the photograph gate for a question nobody
                    could start. Without it the gate is a trap: there is
                    nothing to photograph, and photographing a blank page
                    would read as an attempt. */}
                {!submitted && photos === 0 && (
                  notDone ? (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                      <MinusCircle size={13} className="text-slate-400" />
                      You have marked this one as not attempted.
                      <button onClick={() => setNotAttempted(code, false)}
                        className="underline font-medium hover:text-slate-900">
                        I want to try it after all
                      </button>
                    </div>
                  ) : notDone === false && confirmSkip === code ? (
                    <div className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5">
                      <p className="text-sm font-semibold text-slate-800">
                        Mark {code} as not attempted?
                      </p>
                      <p className="text-xs text-slate-600 mt-0.5">
                        It will score nothing, and your tutor will see that you did not do it
                        rather than that you got it wrong. You can change your mind until you hand in.
                      </p>
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => { setNotAttempted(code, true); setConfirmSkip(null); }}
                          className="text-xs font-semibold bg-slate-800 text-white rounded-lg px-3 py-1.5">
                          Yes, I did not do this one
                        </button>
                        <button onClick={() => setConfirmSkip(null)}
                          className="text-xs text-slate-600 border border-slate-300 rounded-lg px-3 py-1.5">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmSkip(code)}
                      className="text-xs inline-flex items-center gap-1.5 text-slate-500 border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">
                      <MinusCircle size={12} /> I did not attempt this one
                    </button>
                  )
                )}

                {submitted && (
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    {msStatus === "released" ? (
                      <button
                        onClick={() => setShowing((v) => ({ ...v, [`${code}-mark_scheme`]: !v[`${code}-mark_scheme`] }))}
                        className="text-xs inline-flex items-center gap-1 border border-green-300 bg-green-50 text-green-800 rounded-lg px-2.5 py-1.5 font-semibold">
                        <Check size={12} />
                        {showing[`${code}-mark_scheme`] ? "Hide the mark scheme" : "Show the mark scheme"}
                      </button>
                    ) : (
                      <button onClick={() => request(code, "mark_scheme")}
                        className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">
                        Show me the mark scheme
                      </button>
                    )}

                    {maStatus === "released" ? (
                      <button
                        onClick={() => setShowing((v) => ({ ...v, [`${code}-model_answer`]: !v[`${code}-model_answer`] }))}
                        className="text-xs inline-flex items-center gap-1 border border-green-300 bg-green-50 text-green-800 rounded-lg px-2.5 py-1.5 font-semibold">
                        <Check size={12} />
                        {showing[`${code}-model_answer`] ? "Hide the model answer" : "Show the model answer"}
                      </button>
                    ) : maStatus === "pending" ? (
                      <span className="text-xs inline-flex items-center gap-1 border border-amber-300 bg-amber-50 text-amber-800 rounded-lg px-2.5 py-1.5 font-semibold">
                        <Clock size={12} /> Waiting for your tutor
                      </span>
                    ) : (
                      <button onClick={() => request(code, "model_answer")}
                        className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">
                        Ask for the model answer
                      </button>
                    )}

                    {/* Wraps, because a 12 mark question is 13 buttons and
                        they must not push the row off a phone screen. */}
                    <span className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                      <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                        <ClipboardCheck size={12} /> Give yourself
                        {outOf != null ? ` (out of ${outOf})` : ""}
                      </span>
                      {/* One button per mark the question is actually worth.
                          It was 0-5 on every question, so a 2 mark question
                          offered 4 impossible answers and a 7 mark question
                          could not be given full marks at all. */}
                      {marksLadder.map((n) => (
                        <button key={n} onClick={() => selfMark(code, n)}
                          className={`text-xs w-7 h-7 rounded-md border ${
                            mine === n ? "bg-slate-800 text-white border-slate-800" : "border-slate-300 hover:bg-slate-50"}`}>
                          {n}
                        </button>
                      ))}
                    </span>
                  </div>
                )}

                {showing[`${code}-mark_scheme`] && (
                  <AnswerPanel assignmentId={id} code={code} kind="mark_scheme" />
                )}
                {showing[`${code}-model_answer`] && (
                  <AnswerPanel assignmentId={id} code={code} kind="model_answer" />
                )}

                {theirs !== undefined && (
                  <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    Your tutor gave you <strong>{theirs}</strong>
                    {d.tutor_comments[code] ? ` — ${d.tutor_comments[code]}` : ""}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* The hand-in, again, at the end.
          The one at the top is for a student who already knows they are
          finished. This one is where they actually arrive: after the last
          question. It says what it locks before it locks it, because the
          first version simply did it and a child cannot undo a hand-in. */}
      {!submitted ? (
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
          {!confirming ? (
            <>
              <p className="font-semibold text-slate-800">Finished?</p>
              <p className="text-sm text-slate-500 mt-1">
                {withWorking} of {d.question_codes.length} questions have a photo of your working, and you
                have typed {typed} answer{typed === 1 ? "" : "s"}.
                {Object.keys(skipped).length > 0 &&
                  ` ${Object.keys(skipped).length} marked as not attempted.`}
              </p>

              {/* The gate, said out loud. A disabled button with no reason is
                  a dead end for a child working on their own in the evening. */}
              {missingPhotos.length > 0 ? (
                <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                  <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                    <Camera size={14} /> Still need a photo of your working
                  </p>
                  <p className="text-sm text-amber-800 mt-0.5">
                    {missingPhotos.join(", ")} — photograph {missingPhotos.length === 1 ? "it" : "them"} with
                    your phone using the QR code above. If you did not do one, use
                    <strong> “I did not attempt this one”</strong> on that question.
                  </p>
                </div>
              ) : null}

              <button onClick={() => setConfirming(true)} disabled={missingPhotos.length > 0}
                className="mt-3 inline-flex items-center gap-2 bg-slate-800 text-white text-sm font-semibold rounded-lg px-4 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed">
                <Hand size={15} /> Hand in my work
              </button>
            </>
          ) : (
            <>
              <p className="font-semibold text-slate-800">Hand in {d.title}?</p>
              <ul className="text-sm text-slate-600 mt-2 space-y-1 list-disc pl-5">
                <li>
                  {withWorking} of {d.question_codes.length} questions have a photo
                  {Object.keys(skipped).length > 0
                    ? ` — ${Object.keys(skipped).join(", ")} marked as not attempted`
                    : ""}
                </li>
                <li>{typed} of {d.question_codes.length} answers typed</li>
                <li>After this you cannot change your answers or add photos</li>
                <li>The mark scheme unlocks, so you can mark your own work</li>
              </ul>
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={submit} disabled={busy}
                  className="inline-flex items-center gap-2 bg-slate-800 text-white text-sm font-semibold rounded-lg px-4 py-2.5 disabled:opacity-50">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Hand size={15} />}
                  {busy ? "Handing in…" : "Yes, hand it in"}
                </button>
                <button onClick={() => setConfirming(false)}
                  className="text-sm text-slate-600 border border-slate-300 rounded-lg px-4 py-2.5">
                  Not yet
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-green-200 bg-green-50 px-4 py-3.5 flex items-start gap-2.5">
          <CheckCircle2 size={18} className="text-green-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-sm text-green-900">
              Handed in{d.submitted_at ? ` on ${new Date(d.submitted_at).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}` : ""}.
            </p>
            <p className="text-sm text-green-800 mt-0.5">
              Your tutor can see your answers and your working. Mark your own work against the mark scheme
              while you wait — see <a href="/student/analytics" className="underline font-medium">your progress</a>.
            </p>
          </div>
        </div>
      )}

      {big && <Lightbox src={big} onClose={() => setBig(null)} />}
      {calcFor && (
        <Calculator
          code={calcFor}
          initialTape={(d.calc_tape || {})[calcFor] || []}
          readOnly={submitted}
          onSave={submitted ? null : (lines) => saveCalculator(calcFor, lines)}
          onClose={() => setCalcFor(null)}
        />
      )}
    </div>
  );
}

export default function StudentAssignments() {
  const [rows, setRows] = useState([]);
  const [soon, setSoon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  // Loading this list is also what unlocks today's worksheet, server-side.
  useEffect(() => {
    api.get("/my/assignments")
      .then((r) => {
        setRows(r.data?.assignments || []);
        setSoon(r.data?.coming_next || []);
      })
      .catch(() => setRows([])).finally(() => setLoading(false));
  }, []);

  return (
    <PortalShell title="My assignments">
      {openId ? (
        <Doing id={openId} onBack={() => setOpenId(null)} />
      ) : (
        <>
          {loading && <p className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 size={15} className="animate-spin" /> Loading…</p>}
          {!loading && rows.length === 0 && (
            <div className="rounded-xl border border-slate-200 bg-white py-14 text-center">
              <BookOpen size={26} className="mx-auto text-slate-300 mb-3" />
              <p className="font-semibold text-slate-600">Nothing set yet</p>
              <p className="text-sm text-slate-400 mt-1">Your tutor will put work here.</p>
            </div>
          )}
          <div className="space-y-2">
            {rows.map((a) => (
              <button key={a.id} onClick={() => setOpenId(a.id)}
                className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3.5 hover:bg-slate-50/70 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="flex-1 min-w-[150px]">
                  <span className="block font-medium text-slate-900">{a.title}</span>
                  <span className="block text-xs text-slate-500 capitalize">
                    {a.kind} · {a.question_count} questions{a.due_date ? ` · due ${a.due_date}` : ""}
                    {a.timed ? ` · ${Math.round((a.time_limit_seconds || 0) / 60)} min` : ""}
                  </span>
                  {/* A programme day says when it shuts, because that is the
                      thing that costs them points if they let it pass. */}
                  {a.closes_after && !a.closed && a.status !== "submitted" && (
                    <span className="block text-xs text-amber-700 font-medium mt-0.5">
                      closes after {a.closes_after}
                    </span>
                  )}
                  {a.forfeit && (
                    <span className="block text-xs text-slate-500 mt-0.5">
                      no points — {a.forfeit}
                    </span>
                  )}
                  {/* Where they got to. A worksheet picked up over three
                      evenings otherwise looks exactly like one never opened,
                      and the only way to find out was to open it. */}
                  {a.status !== "submitted" && (a.answered_count > 0 || a.photographed_count > 0) && (
                    <span className="block text-xs text-slate-400 mt-0.5">
                      {a.photographed_count} of {a.question_count} photographed
                      {a.answered_count > 0 ? ` · ${a.answered_count} answered` : ""}
                      {a.not_attempted_count > 0 ? ` · ${a.not_attempted_count} not attempted` : ""}
                    </span>
                  )}
                </span>
                {a.closed
                  ? <span className="text-xs text-slate-500 inline-flex items-center gap-1 font-semibold"><Lock size={13} /> closed</span>
                  : a.status === "submitted"
                    ? <span className="text-xs text-green-700 inline-flex items-center gap-1 font-semibold"><Check size={13} /> handed in</span>
                    : (a.answered_count > 0 || a.photographed_count > 0)
                      ? <span className="text-xs text-blue-700 inline-flex items-center gap-1 font-semibold"><Clock size={13} /> carry on</span>
                      : <span className="text-xs text-amber-700 inline-flex items-center gap-1 font-semibold"><Clock size={13} /> to do</span>}
              </button>
            ))}
          </div>

          {/* What unlocks next. It exists to give a reason to come back
              tomorrow; the questions themselves are not created yet, so
              there is nothing here to read ahead. */}
          {soon.length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">
                Coming up
              </p>
              <div className="space-y-2">
                {soon.map((s, i) => (
                  <div key={i}
                    className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <Lock size={14} className="text-slate-400 shrink-0" />
                    <span className="flex-1 min-w-[150px]">
                      <span className="block font-medium text-slate-500">{s.title}</span>
                      <span className="block text-xs text-slate-400 capitalize">
                        {s.kind} · {s.question_count} questions
                      </span>
                    </span>
                    <span className="text-xs font-semibold text-slate-500">
                      {s.days_away === 1 ? "unlocks tomorrow"
                        : `unlocks in ${s.days_away} days`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </PortalShell>
  );
}
